import {
  type SocialConnection,
  type SocialPlatform,
  SUPPORTED_SOCIAL_PLATFORMS,
} from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Info, Loader2, RefreshCw } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useConnections,
  useCreatePortalLink,
  useDisconnectAccount,
  useRefreshConnections,
} from '~/entities/crossposting/model/use-connections'
import { ConnectHelpButton } from '~/features/crossposting/components/crossposting-connect-help'
import { SocialPlatformCard } from '~/features/crossposting/components/social-platform-card'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

function disconnectedConnection(platform: SocialPlatform): SocialConnection {
  return {
    platform,
    status: 'disconnected',
    username: null,
    displayName: null,
    avatarUrl: null,
    channelName: null,
    connectedAt: null,
  }
}

export function ConnectAccounts({
  listingId,
  justReturned,
}: {
  listingId: string
  justReturned?: boolean
}) {
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data, isLoading } = useConnections(businessId, listingId)
  const refresh = useRefreshConnections(businessId, listingId)
  const portalLink = useCreatePortalLink(businessId, listingId)
  const disconnect = useDisconnectAccount(businessId, listingId)

  const refreshMutate = refresh.mutate
  const didAutoRefresh = useRef(false)
  useEffect(() => {
    if (justReturned && businessId && !didAutoRefresh.current) {
      didAutoRefresh.current = true
      refreshMutate(undefined, {
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      })
    }
  }, [justReturned, businessId, refreshMutate])

  const handleConnect = (platform: SocialPlatform) => {
    portalLink.mutate(platform, {
      onSuccess: ({ url }) => {
        window.location.href = url
      },
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })
  }

  const handleDisconnect = (platform: SocialPlatform) => {
    disconnect.mutate(platform, {
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })
  }

  const byPlatform = new Map((data?.connections ?? []).map((c) => [c.platform, c]))
  const connections = SUPPORTED_SOCIAL_PLATFORMS.map(
    (p) => byPlatform.get(p) ?? disconnectedConnection(p),
  )

  const backLink = (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <Link to="/dashboard/crossposting/$listingId" params={{ listingId }}>
        <ArrowLeft className="h-4 w-4" />
        {m.crosspostingBackToTools()}
      </Link>
    </Button>
  )

  const refreshAction = (
    <Button
      variant="outline"
      size="sm"
      onClick={() =>
        refresh.mutate(undefined, {
          onError: (error) => toast.error(getErrorMessageFromError(error)),
        })
      }
      disabled={refresh.isPending}
    >
      {refresh.isPending ? (
        <RefreshCw className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      {m.crosspostingRefresh()}
    </Button>
  )

  if (isBusinessLoading || isLoading) {
    return (
      <div className="space-y-6">
        {backLink}
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {backLink}
      <PageHeader
        title={m.crosspostingConnectTitle()}
        description={m.crosspostingConnectDescription()}
        action={
          <div className="flex items-center gap-2">
            <ConnectHelpButton />
            {refreshAction}
          </div>
        }
      />

      <Card className="flex gap-3 p-4">
        <Info className="h-5 w-5 shrink-0 text-brand" />
        <div className="space-y-1.5">
          <p className="font-medium text-sm">{m.crosspostingConnectHintTitle()}</p>
          <ul className="list-disc space-y-1 pl-4 text-muted-foreground text-sm">
            <li>{m.crosspostingConnectHint1()}</li>
            <li>{m.crosspostingConnectHint2()}</li>
            <li>{m.crosspostingConnectHint3()}</li>
          </ul>
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        {connections.map((connection) => (
          <SocialPlatformCard
            key={connection.platform}
            connection={connection}
            onConnect={() => handleConnect(connection.platform)}
            onDisconnect={() => handleDisconnect(connection.platform)}
            isConnecting={portalLink.isPending && portalLink.variables === connection.platform}
            isDisconnecting={disconnect.isPending && disconnect.variables === connection.platform}
          />
        ))}
      </div>

      {portalLink.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {m.crosspostingConnect()}…
        </p>
      )}
    </div>
  )
}
